import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/providers/auth";
import { useDomainServices } from "@/providers/DomainServices";
import { boardDisplayName, isObjectRecord } from "@/shared/domain/boards";
import {
  isPanelUpdateOperation,
  isStageUpdateOperation,
  loadBoardAuthoringService,
  type PatchOutcome,
  type UpdateOperation,
  type UpdateRequest,
} from "@/shared/domain/boards/boardAuthoringService";
import { type BoardCalendar } from "@/shared/domain/boards/boardCalendar";
import {
  useBoard,
  useViewerControlChoices,
} from "@/shared/domain/boards/hooks";
import { panelPhrase } from "@/shared/domain/boards/panelCaption";
import { declaredStageName } from "@/shared/domain/boards/stageName";
import {
  ConfirmationAlert,
  ConfirmationBullets,
  ConfirmationCard,
  ConfirmationField,
  ConfirmationFields,
  ConfirmationLead,
  ConfirmationPanel,
} from "@/shared/foundation/primitives";
import { LoggerService } from "@/shared/services/LoggerService";
import { capitalizeFirst } from "@/shared/text/capitalizeFirst";

import { BoardCalendarField } from "./BoardCalendarField";
import {
  calendarOfDefinitionMember,
  declarationIdOf,
  definitionNames,
  describeCalendar,
  describePanel,
  describeSavedPanelFaults,
  describeSections,
  describeSetPanelsFilteredByControl,
  describeSetParticipation,
  describeStage,
  describeStageWork,
  describeTimeRange,
  type DefinitionNames,
} from "./boardConfirmationText";
import {
  ApprovalNote,
  CheckFailures,
  DryRunVerdict,
  PanelDryRunStatus,
  StageDryRunStatus,
} from "./BoardDryRunStatus";
import type { ConfirmationContentProps } from "./ClientTools";
import { toUpdateRequest, type UpdateBoardInput } from "./updateBoardInput";
import {
  useBoardDryRun,
  useDryRunServices,
  type BoardDryRunState,
} from "./useBoardDryRun";
import { useControlCustomFields } from "./useControlCustomFields";

/**
 * Read-only preview of the changes the model wants to make to a board,
 * under the board's name: each stage and panel change with its dry-run
 * status beside it, and, when the update replaces the sections list, that
 * list compared with the board's own.
 *
 * The changes render from the tool input on first paint. The authoring
 * service's `patch` then produces the definition the update would save, and
 * the dry run (the same one `execute` reads after approval) runs every stage
 * of that definition and checks its panels, skipping the rendering checks of
 * a panel unchanged from the stored board once its stages pass; both fill in
 * without delaying Approve. Both recompute when the board changes while the
 * card is open, so the card describes the definition `execute` would save
 * rather than the one the board held at mount, and `execute` reads the
 * report the card computed for that same stored definition.
 *
 * A request the tool would refuse (a malformed operation, an unknown panel
 * id) says so above the card. The note on what approving does shows only
 * once `patch` has produced the definition the update would save.
 */
export function UpdateBoardConfirmation({
  params,
}: ConfirmationContentProps<UpdateBoardInput>) {
  const parsed = toUpdateRequest(params);
  const request = "request" in parsed ? parsed.request : undefined;
  const patch = usePatchedDefinition(request);
  const boardQuery = useBoard(request?.boardId ?? null);
  const board = boardQuery.data;
  const viewerChoices = useViewerControlChoices(request?.boardId);
  const dryRun = useBoardDryRun(
    patch?.status === "patched" ? patch.definition : undefined,
    patch?.status === "patched" ? patch.storedDefinition : undefined,
    viewerChoices,
  );
  const refused = "problem" in parsed || patch?.status === "refused";
  const problem = requestProblem(
    boardQuery.error !== null,
    refused,
    patch?.status === "refused" ? patch.cardReasons : undefined,
  );
  // `names` searches the definition the update would save first, so a stage
  // or panel the update adds is named, then, until the patch produces that
  // definition, the stages list the update replaces the board's with, then
  // the stored definition, which names what the update removes;
  // `storedNames` searches the stored and saved definitions the other way
  // round.
  const definitionAfterUpdate =
    patch?.status === "patched" ? patch.definition : undefined;
  const storedDefinition =
    patch?.status === "patched"
      ? patch.storedDefinition
      : board?.board_definition;
  const requestedStages = { stages: request?.stages ?? params.stages };
  // The stages the operations add or change, so the custom fields their
  // controls name start loading before the patch. `names` does not search
  // them: they are not in the board's order, which decides the stage a
  // stage that lists no inputs reads (`defaultInput`).
  const operationStages = {
    stages: (request?.operations ?? []).flatMap((operation) =>
      "stage" in operation ? [operation.stage] : [],
    ),
  };
  const customFieldsOf = useControlCustomFields(
    definitionAfterUpdate,
    requestedStages,
    storedDefinition,
    operationStages,
  );
  const names = definitionNames(
    customFieldsOf,
    definitionAfterUpdate,
    requestedStages,
    storedDefinition,
  );
  const storedNames = definitionNames(
    customFieldsOf,
    storedDefinition,
    definitionAfterUpdate,
  );
  // The zone a custom time range's bounds are shown in: the calendar of the
  // definition the update would save, and until the patch produces it, the
  // update's own calendar or the default.
  const calendarAfterUpdate = calendarOfDefinitionMember(
    patch?.status === "patched"
      ? calendarMemberOf(patch.definition)
      : params.calendar,
  );

  return (
    <ConfirmationPanel label="Update board preview">
      <ConfirmationLead>Apply these changes to the board?</ConfirmationLead>
      <RequestProblem problem={problem} />
      <DryRunVerdict
        dryRun={dryRun}
        shownOnCard={[describeCalendar(params.calendar)?.problem]}
      />
      <ConfirmationCard
        title={board !== undefined ? boardDisplayName(board) : "Board"}
      >
        <ConfirmationFields>
          <TitleField title={params.title} />
          <TimeRangeField
            timeRange={params.time_range}
            calendar={calendarAfterUpdate}
          />
          <BoardCalendarField
            calendar={params.calendar}
            label="New time zone and week start"
          />
          <SectionsField
            sections={request?.sections ?? params.sections}
            stored={
              patch?.status === "patched"
                ? storedSectionsOf(patch.storedDefinition)
                : undefined
            }
          />
          <StagesField
            stages={request?.stages ?? params.stages}
            names={names}
            timeZone={calendarAfterUpdate.timeZone}
            dryRun={dryRun}
          />
          <OperationsField
            operations={request?.operations}
            patch={patch}
            names={names}
            storedNames={storedNames}
            timeZone={calendarAfterUpdate.timeZone}
            dryRun={dryRun}
          />
          <RewrittenDeclarations
            patch={patch}
            operations={request?.operations}
            names={names}
            dryRun={dryRun}
          />
          <CheckFailures
            dryRun={dryRun}
            panelName={(panel, index) =>
              capitalizeFirst(
                panel.id === null
                  ? panelPhraseAt(definitionAfterUpdate, panel.index ?? index)
                  : storedNames.panel(panel.id),
              )
            }
          />
          <SavedPanelFaults
            dryRun={dryRun}
            definition={definitionAfterUpdate}
            names={names}
          />
        </ConfirmationFields>
      </ConfirmationCard>
      <ApprovalNote
        dryRun={dryRun}
        definitionKnown={patch?.status === "patched"}
      >
        Approving first loads every stage and every changed panel once, to check
        that each loads without an error. The board is saved only if all of them
        do; otherwise nothing is saved and the chat is told what failed.
      </ApprovalNote>
    </ConfirmationPanel>
  );
}

/**
 * The definition the update would save, from the authoring service's
 * `patch`.
 *
 * The patch reads the board, so it is keyed on that board's revision as
 * well as the request: a write of the definition advances the revision, and
 * the card recomputes against what the board holds after it rather than
 * against the revision the card first read. A write made from this browser
 * reaches the revision immediately, because the board's own writes replace
 * what the board cache holds; someone else's write reaches it when that
 * cache reads the board again.
 *
 * The card waits for the board before patching, so the patch runs once per
 * revision instead of once before the board arrives and again after.
 */
function usePatchedDefinition(
  request: UpdateRequest | undefined,
): PatchOutcome | undefined {
  const { currentOrganization } = useAuth();
  const { boardService } = useDomainServices();
  const services = useDryRunServices();
  const queryClient = useQueryClient();
  const orgId = currentOrganization?.org_id;
  const board = useBoard(request?.boardId ?? null);
  const revision = board.data?.revision;
  const query = useQuery({
    queryKey: [
      "boardData",
      "authoringPatch",
      orgId,
      request === undefined ? undefined : JSON.stringify(request),
      revision,
    ],
    enabled:
      request !== undefined && orgId !== undefined && revision !== undefined,
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
    queryFn: async (): Promise<PatchOutcome> => {
      if (request === undefined || orgId === undefined) {
        throw new Error("Nothing to patch");
      }
      try {
        const service = await loadBoardAuthoringService();
        return await service.patch(
          request,
          {
            orgId,
            services,
            queryClient,
            // A signal nothing aborts, so the card unmounting does not throw
            // away a patch in flight; `boardDryRunQueryOptions` says why.
            signal: new AbortController().signal,
          },
          { boardService, orgId },
        );
      } catch (error) {
        // The card shows a person its own sentence and passes the error's
        // message to the chat, so the console is where the error itself is.
        LoggerService.warn(
          "The chat's changes to a board could not be applied.",
          error,
        );
        throw error;
      }
    },
  });
  if (query.isError) {
    return { status: "refused", reasons: [describe(query.error)] };
  }
  return query.data;
}

function describe(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/**
 * Why the request will not succeed, for the alert above the card, or null
 * when nothing is known to stop it. A refusal's `cardReasons`, when it has
 * them, are the words the board's own editors give a person for it; the
 * other reasons are written for the chat, which receives them when the
 * person approves.
 */
function requestProblem(
  boardUnreadable: boolean,
  refused: boolean,
  cardReasons: readonly string[] | undefined,
): string | null {
  if (boardUnreadable) {
    return "This board could not be loaded, so the changes can’t be checked here. Approving still checks them before saving.";
  }
  if (!refused) {
    return null;
  }
  const outcome = "Approving changes nothing and tells the chat why.";
  if (cardReasons !== undefined && cardReasons.length > 0) {
    return `This request won’t succeed. ${cardReasons.join(" ")} ${outcome}`;
  }
  return `This request won’t succeed: the chat’s changes can’t be applied to this board. ${outcome}`;
}

function RequestProblem({ problem }: { problem: string | null }) {
  if (problem === null) {
    return null;
  }
  return <ConfirmationAlert>{problem}</ConfirmationAlert>;
}

function TitleField({ title }: { title: unknown }) {
  if (typeof title !== "string") {
    return null;
  }
  return <ConfirmationField label="New title">{title}</ConfirmationField>;
}

function TimeRangeField({
  timeRange,
  calendar,
}: {
  timeRange: unknown;
  calendar: BoardCalendar;
}) {
  const label = describeTimeRange(timeRange, calendar);
  if (label === undefined) {
    return null;
  }
  return <ConfirmationField label="New time range">{label}</ConfirmationField>;
}

/**
 * The sections list the update replaces the board's with, by title, each
 * marked with the selections it declares and with what differs from the
 * stored section (added, moved or resized), and the stored sections the list
 * drops.
 *
 * The card passes the parsed request's list, which is the one the update
 * applies even when the model sent it as JSON text, and falls back to the
 * tool input only when the request could not be parsed.
 */
function SectionsField({
  sections,
  stored,
}: {
  sections: unknown;
  stored: unknown[] | undefined;
}) {
  if (!Array.isArray(sections)) {
    return null;
  }
  if (sections.length === 0) {
    return (
      <ConfirmationField label="Sections">(no sections)</ConfirmationField>
    );
  }
  return (
    <ConfirmationField label={`Sections (${sections.length})`}>
      {describeSections(sections, stored)}
    </ConfirmationField>
  );
}

/** The `calendar` member of the definition the update would save. */
function calendarMemberOf(definition: unknown): unknown {
  return typeof definition === "object" && definition !== null
    ? (definition as { calendar?: unknown }).calendar
    : undefined;
}

/**
 * The stored definition's sections: empty for a board that stores none, so
 * every proposed section is marked added, and undefined only when there is
 * no stored definition, so nothing is compared.
 */
function storedSectionsOf(definition: unknown): unknown[] | undefined {
  if (typeof definition !== "object" || definition === null) {
    return undefined;
  }
  const { sections } = definition as { sections?: unknown };
  return isUnknownList(sections) ? sections : [];
}

/**
 * A list read from a stored definition, whose members are unknown until
 * something reads them. `Array.isArray` on its own widens them to `any`,
 * which would let a caller treat a stored section as anything at all.
 */
function isUnknownList(value: unknown): value is unknown[] {
  return Array.isArray(value);
}

/** The stages list the update replaces the board's with, each with its
 *  dry-run status, a control's values written in `timeZone`. */
function StagesField({
  stages,
  names,
  timeZone,
  dryRun,
}: {
  stages: unknown;
  names: DefinitionNames;
  timeZone: string;
  dryRun: BoardDryRunState;
}) {
  if (!Array.isArray(stages)) {
    return null;
  }
  if (stages.length === 0) {
    return (
      <ConfirmationField label="Stages after this change">
        (none: every stage removed)
      </ConfirmationField>
    );
  }
  return (
    <ConfirmationField label={`Stages after this change (${stages.length})`}>
      <ConfirmationBullets>
        {stages.map((stage, index) => (
          <li key={index}>
            {describeStage(stage, names, timeZone)}
            <StageDryRunStatus
              dryRun={dryRun}
              stageId={declarationIdOf(stage)}
              stageIndex={index}
            />
          </li>
        ))}
      </ConfirmationBullets>
    </ConfirmationField>
  );
}

/** One line per operation: the patch's sentence for it once the patch is
 *  known and words it, else the sentence the request's own ids give. */
function OperationsField({
  operations,
  patch,
  names,
  storedNames,
  timeZone,
  dryRun,
}: {
  operations: UpdateOperation[] | undefined;
  patch: PatchOutcome | undefined;
  names: DefinitionNames;
  storedNames: DefinitionNames;
  timeZone: string;
  dryRun: BoardDryRunState;
}) {
  if (operations === undefined) {
    return null;
  }
  if (operations.length === 0) {
    return (
      <ConfirmationField label="Changes">
        (no stage or panel changes)
      </ConfirmationField>
    );
  }
  const patchSentences =
    patch?.status === "patched" ? patch.operationSentences : [];
  return (
    <ConfirmationField label={`Changes (${operations.length})`}>
      <ConfirmationBullets>
        {operations.map((operation, index) => (
          <li key={index}>
            {patchSentences[index] ??
              describeOperation(operation, names, storedNames, timeZone)}
            <OperationStatus operation={operation} dryRun={dryRun} />
          </li>
        ))}
      </ConfirmationBullets>
    </ConfirmationField>
  );
}

/**
 * The faults the saved board already has in panels the update leaves as
 * saved, one line per panel. They do not block the update, and a panel no
 * operation names has no other line on the card. `definition` is the one the
 * dry run ran on, which names a panel that carries no id.
 */
function SavedPanelFaults({
  dryRun,
  definition,
  names,
}: {
  dryRun: BoardDryRunState;
  definition: unknown;
  names: DefinitionNames;
}) {
  if (dryRun.status !== "ready") {
    return null;
  }
  const lines = dryRun.report.panels.flatMap((panel, index) => {
    const name =
      panel.id === null
        ? panelPhraseAt(definition, panel.index ?? index)
        : names.panel(panel.id);
    const line = describeSavedPanelFaults(panel, capitalizeFirst(name));
    return line === undefined ? [] : [{ key: index, line }];
  });
  if (lines.length === 0) {
    return null;
  }
  return (
    <ConfirmationField label="Problems already in the saved board, which do not stop this update">
      <ConfirmationBullets>
        {lines.map(({ key, line }) => (
          <li key={key}>{line}</li>
        ))}
      </ConfirmationBullets>
    </ConfirmationField>
  );
}

/**
 * The stages and panels no operation names whose declarations the patch
 * still rewrote, such as a stage whose SQL reads a renamed stage; the save
 * stores them too.
 */
function RewrittenDeclarations({
  patch,
  operations,
  names,
  dryRun,
}: {
  patch: PatchOutcome | undefined;
  operations: UpdateOperation[] | undefined;
  names: DefinitionNames;
  dryRun: BoardDryRunState;
}) {
  if (
    patch?.status !== "patched" ||
    !operations?.some(isStageUpdateOperation)
  ) {
    return null;
  }
  const stageTargets = new Set(
    operations.filter(isStageUpdateOperation).map((op) => op.stage_id),
  );
  const panelTargets = new Set(
    operations.flatMap((op) =>
      isPanelUpdateOperation(op) ? [op.panel_id] : [],
    ),
  );
  const stages = rewrittenDeclarations(
    patch.storedDefinition,
    patch.definition,
    "stages",
    stageTargets,
  );
  const panels = rewrittenDeclarations(
    patch.storedDefinition,
    patch.definition,
    "panels",
    panelTargets,
  );
  if (stages.length === 0 && panels.length === 0) {
    return null;
  }
  return (
    <ConfirmationField label="Other stages and panels this update changes">
      <ConfirmationBullets>
        {stages.map((entry) => (
          <li key={`stage:${entry.indexBeforeUpdate}`}>
            {entry.id === undefined
              ? stageWithoutIdLabel(patch.definition, entry)
              : capitalizeFirst(names.stagePhrase(entry.id))}
            <StageDryRunStatus
              dryRun={dryRun}
              stageId={entry.id}
              stageIndex={entry.index}
            />
          </li>
        ))}
        {panels.map((entry) => (
          <li key={`panel:${entry.indexBeforeUpdate}`}>
            {capitalizeFirst(
              entry.id === undefined
                ? panelPhraseAt(patch.definition, entry.index)
                : names.panel(entry.id),
            )}
            <PanelDryRunStatus
              dryRun={dryRun}
              panelId={entry.id}
              panelIndex={entry.index}
            />
          </li>
        ))}
      </ConfirmationBullets>
    </ConfirmationField>
  );
}

interface RewrittenDeclaration {
  id?: string;
  indexBeforeUpdate: number;
  index: number;
}

/** The panel at `index` of `definition`, which carries no id, named by its
 *  title or as `Panel N` (`panelPhrase`). */
function panelPhraseAt(definition: unknown, index: number): string {
  const panel = declarationsOf(definition, "panels")[index]?.raw;
  return panelPhrase(isObjectRecord(panel) ? panel.title : undefined, index);
}

/** A rewritten stage that carries no id, named by its name in `definition`,
 *  or else by its place in the board's JSON as saved. */
function stageWithoutIdLabel(
  definition: unknown,
  entry: RewrittenDeclaration,
): string {
  const name = declaredStageName(
    declarationsOf(definition, "stages")[entry.index]?.raw,
  );
  return name === undefined
    ? `Stage ${entry.indexBeforeUpdate + 1} in the board's JSON`
    : `The stage "${name}"`;
}

/**
 * The declarations in `member` that no operation targets and whose JSON
 * differs between `before` and `after`. Declarations are paired by id in
 * order of appearance: an operation cannot name a declaration that shares
 * its id or has none, so the nth such declaration before the update is
 * compared with the nth after it.
 */
function rewrittenDeclarations(
  before: unknown,
  after: unknown,
  member: "stages" | "panels",
  targets: ReadonlySet<string>,
): RewrittenDeclaration[] {
  const entriesBeforeUpdateById = new Map<
    string | undefined,
    { raw: unknown; index: number }[]
  >();
  for (const entry of declarationsOf(before, member)) {
    const group = entriesBeforeUpdateById.get(entry.id) ?? [];
    group.push(entry);
    entriesBeforeUpdateById.set(entry.id, group);
  }
  return declarationsOf(after, member).flatMap((entry) => {
    const entryBeforeUpdate = entriesBeforeUpdateById.get(entry.id)?.shift();
    if (
      entryBeforeUpdate === undefined ||
      (entry.id !== undefined && targets.has(entry.id)) ||
      JSON.stringify(entryBeforeUpdate.raw) === JSON.stringify(entry.raw)
    ) {
      return [];
    }
    return [
      {
        id: entry.id,
        indexBeforeUpdate: entryBeforeUpdate.index,
        index: entry.index,
      },
    ];
  });
}

function declarationsOf(
  definition: unknown,
  member: "stages" | "panels",
): { id?: string; raw: unknown; index: number }[] {
  if (typeof definition !== "object" || definition === null) {
    return [];
  }
  const entries: unknown = (definition as Record<string, unknown>)[member];
  if (!isUnknownList(entries)) {
    return [];
  }
  return entries.map((raw, index) => ({
    raw,
    index,
    id: declarationIdOf(raw),
  }));
}

function OperationStatus({
  operation,
  dryRun,
}: {
  operation: UpdateOperation;
  dryRun: BoardDryRunState;
}) {
  if (operation.op === "remove" || operation.op === "remove_stage") {
    return null;
  }
  if (isStageUpdateOperation(operation)) {
    return <StageDryRunStatus dryRun={dryRun} stageId={operation.stage_id} />;
  }
  if (!isPanelUpdateOperation(operation)) {
    return null;
  }
  return <PanelDryRunStatus dryRun={dryRun} panelId={operation.panel_id} />;
}

/**
 * One operation as a sentence. `names` looks in the definition the update
 * would save first, and `storedNames` in the stored one first. The panel an
 * operation changes or removes, and a stage whose name it changes, are named
 * as the board shows them now (`storedNames`), so a panel the update gives a
 * title is still named by the caption the person sees on the canvas. A
 * control's values are written in `timeZone`.
 */
function describeOperation(
  operation: UpdateOperation,
  names: DefinitionNames,
  storedNames: DefinitionNames,
  timeZone: string,
): string {
  switch (operation.op) {
    case "replace":
      return `Change ${storedNames.panel(operation.panel_id)} to ${describePanel(operation.panel, names)}`;
    case "add":
      return `Add ${describePanel(operation.panel, names)}`;
    case "remove":
      return `Remove ${storedNames.panel(operation.panel_id)}`;
    case "set_participation_and_publish":
      return describeSetParticipation(operation, storedNames);
    case "add_stage":
      return `Add ${names.declaredStagePhrase(operation.stage)} ${operation.after_stage_id === undefined ? "at the end" : `after ${names.stage(operation.after_stage_id)}`}: ${describeStageWork(operation.stage, names, timeZone)}`;
    case "replace_stage": {
      const before = storedNames.stagePhrase(operation.stage_id);
      const after = declaredStageName(operation.stage);
      const renamed =
        after === undefined ||
        storedNames.stage(operation.stage_id) === `"${after}"`
          ? ""
          : ` and name it "${after}"`;
      return `Change ${before}${renamed}: ${describeStageWork(operation.stage, names, timeZone)}`;
    }
    case "rename_stage":
      return `Rename ${storedNames.stagePhrase(operation.stage_id)} to "${operation.name}" and update the stages and panels that read it`;
    case "remove_stage":
      return `Remove ${names.stagePhrase(operation.stage_id)}`;
    case "set_panels_filtered_by_control":
      return describeSetPanelsFilteredByControl(operation, names);
  }
}
